import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import anime from "animejs";
import { PixiSettings, FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import { ASPECT_RATIO_BREAKPOINTS } from "../../../../Contexts/DimensionsContext";
import { createButtonPressAnimation } from "../../../../utilities";

// Opaque full-screen backdrop. The loader is torn down when this appears, so
// this layer is what the player actually sees behind the panel.
//
// Dark GREY, not black: the panel above it is black at 0.8 alpha, exactly as
// in WarningPopup, and over a black backdrop it would be invisible. That popup
// gets its contrast for free by sitting over the running game; this one has to
// provide it.
// Lighter than it looks: the 0.6 black overlay sits on top of it, so the value
// the player actually sees is roughly 40% of this. 0x555555 lands near 0x22
// after the overlay, against a panel at about 0x06 — that gap is the contrast.
const SCREEN_BACKGROUND_COLOR = 0x555555;

// ── Identical to WarningPopup / InfoPopup ────────────────────────────────────
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150;

const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;

const BUTTON_REF_WIDTH = 160;
const BUTTON_REF_HEIGHT = 64;

const PANEL_REF_WIDTH = 504;
const PANEL_REF_HEIGHT = 342;
const DESKTOP_SCALE_REF = 1.175;

// Same multipliers / base values as WarningPopup ERROR_POPUP_CONFIG
const ERROR_POPUP_CONFIG = {
  REFERENCE_WIDTH: 600,
  basePaddingH: 22,
  basePaddingV: 42,
  baseMessageFontSize: 20,
  baseButtonFontSize: 24,
  baseTextButtonGap: 55,
  baseBorderRadius: 20,
  baseMinEdgePadding: 20,
  baseContentWidth: 370,
  DEVICE_CONFIGS: {
    MOBILE_SMALL: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.575 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.65 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.79 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7125 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7 },
    },
    SMALL_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.725 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.97 },
    },
    WIDE_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.14 },
    },
  },
};

// Layout multipliers identical to WarningPopup
const TEXT_AREA_TOP_MULTIPLIER = 1.0;
const BOTTOM_PADDING_MULTIPLIER = 0.4;
const TEXT_TO_BUTTON_GAP_MULTIPLIER = 0.3;
const BUTTON_EXTRA_LIFT_MULTIPLIER = 0.36;

/**
 * LoadingErrorPopupController — the "init failed, reload" screen.
 *
 * Lifted out of LoaderController unchanged. It lived there for historical
 * reasons and that coupling was the bug: the loader owned the error, so the
 * loader\'s own hand-off could run right past it and carry on into the intro
 * popup. As its own component it is mounted by Stage on the error state alone,
 * and nothing about the loading sequence can dismiss or outrun it.
 *
 * Layout, fonts and multipliers are identical to WarningPopup / InfoPopup.
 */
class LoadingErrorPopupController {
  constructor(container, dimensions, dimensionsContext) {
    this.container = container;
    this.dimensions = dimensions;
    this.dimensionsContext = dimensionsContext;

    this.errorOccurred = false;
    this.errorRoot = null;
    this.errorContainer = null; // content container (scaled)
    this._errorOverlay = null;
    this._errorMessage = null;
    this._errorButtonSprite = null;
    this._isErrorPressAnimating = false;
    this._errorBrightnessFilter = null;

    // Opaque black behind everything, and a blocker: nothing underneath may
    // be clicked once the game has failed to start.
    this._screenBg = new PIXI.Graphics();
    this._screenBg.zIndex = 0;
    this._screenBg.eventMode = "static";
    this._screenBg.on("pointerdown", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
    });
    this.container.addChild(this._screenBg);
    this._updateScreenBackground();
  }

  _updateScreenBackground() {
    if (!this._screenBg || !this.dimensions) return;
    const { width, height } = this.dimensions;
    this._screenBg.clear();
    this._screenBg.beginFill(SCREEN_BACKGROUND_COLOR);
    this._screenBg.drawRect(0, 0, width, height);
    this._screenBg.endFill();
  }

  _getErrorDeviceConfig() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    const width = this.dimensions?.width || 0;
    const configs = ERROR_POPUP_CONFIG.DEVICE_CONFIGS;

    if (device === PixiSettings.DEVICE.DESKTOP) {
      let desktopSize;
      if (width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH) {
        desktopSize = "SMALL_DESKTOP";
      } else if (width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) {
        desktopSize = "WIDE_DESKTOP";
      } else {
        desktopSize = "DESKTOP";
      }
      return (configs[desktopSize] || configs[PixiSettings.DEVICE.DESKTOP])[
        PixiSettings.ORIENTATION.LANDSCAPE
      ];
    }

    if (isMobileSmall) {
      return (
        configs.MOBILE_SMALL?.[orientation] ||
        configs[PixiSettings.DEVICE.MOBILE][orientation]
      );
    }

    return (
      configs[device]?.[orientation] ||
      configs[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE]
    );
  }

  _destroyErrorUI() {
    if (this.errorRoot) {
      try {
        anime.remove(this.errorRoot);
        if (this.errorContainer) anime.remove(this.errorContainer.scale);
        if (this.container?.children?.includes(this.errorRoot)) {
          this.container.removeChild(this.errorRoot);
        }
        this.errorRoot.destroy({ children: true });
      } catch {
        // Silent fail
      }
    }
    this.errorRoot = null;
    this.errorContainer = null;
    this._errorOverlay = null;
    this._errorButtonSprite = null;
    this._isErrorPressAnimating = false;
    this._errorBrightnessFilter = null;
  }

  /**
   * Show error fallback UI — WarningPopup-identical layout, fonts, multipliers,
   * overlay fade + scale-in. Reload button (init failed before game assets).
   */
  showError(message = "Something went wrong.\nPlease reload the game.") {
    this.errorOccurred = true;
    this._errorMessage = message;
    this._buildErrorUI(message, { animate: true });
  }

  _buildErrorUI(message, { animate = false } = {}) {
    this._destroyErrorUI();

    const { width, height } = this.dimensions || { width: 800, height: 600 };
    const centerX = width / 2;
    const centerY = height / 2;

    const deviceConfig = this._getErrorDeviceConfig();
    const deviceScale = deviceConfig?.scaleMultiplier || 1.0;
    const scaledW = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * deviceScale;
    const scaledH = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * deviceScale;
    const maxWidth = width * 0.85;
    const maxHeight = height * 0.85;
    const widthRatio = scaledW > maxWidth ? maxWidth / scaledW : 1;
    const heightRatio = scaledH > maxHeight ? maxHeight / scaledH : 1;
    const popupScale = deviceScale * Math.min(widthRatio, heightRatio);

    const panelWidth = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * popupScale;
    const panelHeight = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * popupScale;
    const paddingH = ERROR_POPUP_CONFIG.basePaddingH * popupScale;
    const paddingV = ERROR_POPUP_CONFIG.basePaddingV * popupScale;
    const buttonFontSize = Math.round(
      ERROR_POPUP_CONFIG.baseButtonFontSize * popupScale,
    );
    const borderRadius = ERROR_POPUP_CONFIG.baseBorderRadius * popupScale;
    const contentWidth = ERROR_POPUP_CONFIG.baseContentWidth * popupScale;
    const buttonWidth = BUTTON_REF_WIDTH * popupScale;
    const buttonHeight = BUTTON_REF_HEIGHT * popupScale;

    // Auto-shrink message font to fit fixed panel (identical to WarningPopup)
    const textAreaTop = paddingV * TEXT_AREA_TOP_MULTIPLIER;
    const textAreaBottom =
      paddingV * BOTTOM_PADDING_MULTIPLIER +
      buttonHeight +
      paddingV * TEXT_TO_BUTTON_GAP_MULTIPLIER;
    const availableTextHeight = panelHeight - textAreaTop - textAreaBottom;

    let messageFontSize = Math.round(
      ERROR_POPUP_CONFIG.baseMessageFontSize * popupScale,
    );
    const measure = (size) => {
      const style = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        breakWords: true,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      });
      return TextMetrics.measureText(message, style).height;
    };
    const measuredHeight = measure(messageFontSize);
    if (measuredHeight > availableTextHeight && measuredHeight > 0) {
      messageFontSize = Math.max(
        10,
        Math.floor(messageFontSize * (availableTextHeight / measuredHeight)),
      );
    }

    const panelX = centerX - panelWidth / 2;
    const panelY = centerY - panelHeight / 2;

    const textAreaTopAbs = panelY + paddingV * TEXT_AREA_TOP_MULTIPLIER;
    const textAreaBottomAbs =
      panelY +
      panelHeight -
      paddingV * BOTTOM_PADDING_MULTIPLIER -
      buttonHeight -
      paddingV * TEXT_TO_BUTTON_GAP_MULTIPLIER;
    const messageY = (textAreaTopAbs + textAreaBottomAbs) / 2;
    const buttonY =
      panelY +
      panelHeight -
      paddingV * (BOTTOM_PADDING_MULTIPLIER + BUTTON_EXTRA_LIFT_MULTIPLIER) -
      buttonHeight / 2;

    const exclamationX = panelX + paddingH * 0.5;
    const exclamationY = panelY + paddingV * 0.15;
    const exclamationFontSize = Math.round(messageFontSize * 1.8);

    // Root holds overlay + content
    this.errorRoot = new PIXI.Container();
    this.errorRoot.name = "loader_error_popup";
    this.errorRoot.zIndex = 10000;
    this.errorRoot.sortableChildren = true;
    this.container.addChild(this.errorRoot);

    // Overlay (blocks clicks)
    this._errorOverlay = new PIXI.Graphics();
    this._errorOverlay.beginFill(OVERLAY_BACKGROUND_COLOR);
    this._errorOverlay.drawRect(0, 0, width, height);
    this._errorOverlay.endFill();
    this._errorOverlay.alpha = animate ? 0 : OVERLAY_BACKGROUND_ALPHA;
    this._errorOverlay.eventMode = "static";
    this._errorOverlay.zIndex = 10000;
    this._errorOverlay.on("pointerdown", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
    });
    this.errorRoot.addChild(this._errorOverlay);

    // Content container — scale animation pivots around screen center
    this.errorContainer = new PIXI.Container();
    this.errorContainer.name = "popup_content";
    this.errorContainer.zIndex = 10001;
    this.errorContainer.x = centerX;
    this.errorContainer.y = centerY;
    this.errorContainer.pivot.set(centerX, centerY);
    if (animate) {
      this.errorContainer.scale.set(0);
    }
    this.errorRoot.addChild(this.errorContainer);

    // Panel
    const panel = new PIXI.Graphics();
    panel.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, borderRadius);
    panel.endFill();
    this.errorContainer.addChild(panel);

    // Exclamation
    const exclText = new PIXI.Text("!", {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: exclamationFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    exclText.anchor.set(0, 0);
    exclText.x = exclamationX;
    exclText.y = exclamationY;
    exclText.resolution = 2;
    this.errorContainer.addChild(exclText);

    // Message
    const messageText = new PIXI.Text(message, {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: messageFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: contentWidth,
      breakWords: true,
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    messageText.anchor.set(0.5);
    messageText.x = centerX;
    messageText.y = messageY;
    messageText.resolution = 2;
    this.errorContainer.addChild(messageText);

    // Button container (identical structure to WarningPopup OK button)
    const buttonContainer = new PIXI.Container();
    buttonContainer.name = "ok_button";
    buttonContainer.x = centerX;
    buttonContainer.y = buttonY;
    this.errorContainer.addChild(buttonContainer);

    const hitArea = new PIXI.Graphics();
    hitArea.beginFill(0x000000, 0.01);
    hitArea.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
    hitArea.endFill();
    hitArea.eventMode = "static";
    hitArea.cursor = "pointer";
    hitArea.hitArea = new PIXI.Rectangle(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
    );
    buttonContainer.addChild(hitArea);

    // Button fill = GameRulesOverlay scrollbar track/background (#383838)
    const SCROLLBAR_TRACK_COLOR = 0x383838;
    const btnRadius = Math.min(12, buttonHeight * 0.2);
    const buttonBg = new PIXI.Graphics();
    buttonBg.beginFill(SCROLLBAR_TRACK_COLOR);
    buttonBg.drawRoundedRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
      btnRadius,
    );
    buttonBg.endFill();
    buttonBg.eventMode = "none";
    this._errorButtonSprite = buttonBg;
    buttonContainer.addChild(buttonBg);

    const buttonText = new PIXI.Text("Reload", {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: buttonFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    buttonText.anchor.set(0.5);
    buttonText.resolution = 2;
    buttonText.eventMode = "none";
    buttonContainer.addChild(buttonText);

    this._errorBrightnessFilter = new PIXI.ColorMatrixFilter();
    this._errorBrightnessFilter.brightness(2.0, false);

    hitArea.on("pointerover", () => {
      if (this._errorButtonSprite && this._errorButtonSprite.filters?.length !== 1) {
        this._errorButtonSprite.filters = [this._errorBrightnessFilter];
      }
    });
    hitArea.on("pointerout", () => {
      if (this._errorButtonSprite) {
        this._errorButtonSprite.filters = [];
      }
    });
    hitArea.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (this._isErrorPressAnimating) {
        window.location.reload();
        return;
      }
      this._isErrorPressAnimating = true;
      if (this._errorButtonSprite && this._errorButtonSprite.width !== undefined) {
        createButtonPressAnimation(this._errorButtonSprite, {
          targetWidth: buttonWidth,
          targetHeight: buttonHeight,
          onComplete: () => {
            this._isErrorPressAnimating = false;
            window.location.reload();
          },
        });
      } else {
        window.location.reload();
      }
    });

    if (animate) {
      anime({
        targets: this._errorOverlay,
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
      });
      anime({
        targets: this.errorContainer.scale,
        x: 1,
        y: 1,
        duration: POPUP_SCALE_DURATION,
        delay: POPUP_SCALE_DELAY,
        easing: POPUP_SCALE_EASING,
      });
    }
  }

  updateDimensions(dimensions, dimensionsContext) {
    this.dimensions = dimensions;
    if (dimensionsContext) this.dimensionsContext = dimensionsContext;
    this._updateScreenBackground();
    // Rebuilt rather than re-tweened — the entrance animation must not replay
    // on a resize.
    if (this.errorOccurred && this._errorMessage) {
      this._buildErrorUI(this._errorMessage, { animate: false });
    }
  }

  cleanup() {
    this._destroyErrorUI();
    if (this._screenBg) {
      this._screenBg.removeAllListeners();
      if (this.container?.children?.includes(this._screenBg)) {
        this.container.removeChild(this._screenBg);
      }
      this._screenBg.destroy();
      this._screenBg = null;
    }
  }
}

export default LoadingErrorPopupController;
